/**
 * The shop's unit of time is the minute, and the shop clock counts them
 * fractionally: every engine tick the simulation advances by whatever
 * slice of a minute the pace brings (see `src/sim/TimeFlow.ts`), so a
 * cut, a cure, a stroll down the sidewalk all move continuously.
 * Durations are declared in whole minutes because that's how a
 * woodworker thinks about them; `formatDuration` rounds a span up to
 * whole minutes, so the player never reads a fraction. Anything
 * rendering a span of time goes through it; moments are told only as
 * broadly as `dayPhase`.
 */

/**
 * How many minutes make a working day — the budget between the 7 AM
 * open and the 5 PM close. Spending them is the game: work, trips, and
 * waiting all draw the day down; walking and thinking barely do (see
 * `src/sim/TimeFlow.ts`). When they're gone the shop is closed for the
 * night and the only way forward is driving home.
 */
export const MINUTES_PER_DAY = 600;

/**
 * The overnight, 5 PM back around to 7 AM: fourteen hours of minutes,
 * run as one batch while the player sleeps. Glue cures, listings sell,
 * demand recovers — everything that happens overnight happens because
 * the same systems ran those minutes.
 */
export const NIGHT_MINUTES = 840;

/**
 * A full calendar day of shop time, wall-clock: the working day plus the
 * night. Anything the game quotes "in days" is denominated in this, so
 * "three days" means three mornings from now even though most of those
 * minutes pass in overnight batches.
 */
export const MINUTES_PER_CALENDAR_DAY = MINUTES_PER_DAY + NIGHT_MINUTES;

const MINUTES_PER_HOUR = 60;
/** Float noise a countdown's remainder can carry without reading as a
 * whole extra minute. */
const FRACTION_TOLERANCE = 1e-6;

/** What the day questions read — a structural slice of `GameState` that
 * the Clock singleton also satisfies. */
export interface ClockFacts {
  /** The shop clock's minute, fractional. */
  readonly minute: number;
  /** The minute this morning started on. */
  readonly dayStartMinute: number;
}

/** How many of today's working minutes have been spent. */
export function dayMinutesSpent(clock: ClockFacts): number {
  return clock.minute - clock.dayStartMinute;
}

/** Where today stands, morning through night. */
export function currentDayPhase(clock: ClockFacts): DayPhase {
  return dayPhase(dayMinutesSpent(clock));
}

/**
 * Whether the shop is closed for the night: the day's budget is spent.
 * Operations already running may finish (working overtime is allowed —
 * the day just doesn't end until you drive home), but nothing new
 * starts and idle time stops passing.
 */
export function isNight(clock: ClockFacts): boolean {
  return dayMinutesSpent(clock) >= MINUTES_PER_DAY;
}

/**
 * A per-minute share or chance spread over `minutes` — the slice of a
 * minute an engine tick brings, or a whole forced batch. What is left
 * after each minute compounds, so a minute served whole and a minute
 * served in sixty slices take (or roll) the same: the broom's share of
 * a cell, the street's spawn dice. Exact for a whole minute.
 */
export function shareOverMinutes(perMinute: number, minutes: number): number {
  if (minutes === 1) return perMinute;
  return 1 - Math.pow(1 - perMinute, minutes);
}

/**
 * A span of shop minutes as the player reads it: "8 min", "1h 05m",
 * "2h 30m", "1d 4h". Whole units are left bare ("1h", not "1h 00m") so
 * the common cases read like something a woodworker would say out loud.
 * A fraction rounds up, so a countdown reads "1 min" until the work is
 * actually done.
 */
export function formatDuration(span: number): string {
  const minutes = Math.max(0, Math.ceil(span - FRACTION_TOLERANCE));

  if (minutes >= MINUTES_PER_DAY) {
    const days = Math.floor(minutes / MINUTES_PER_DAY);
    const hours = Math.floor((minutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
    return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
  }
  if (minutes >= MINUTES_PER_HOUR) {
    const hours = Math.floor(minutes / MINUTES_PER_HOUR);
    const rest = minutes % MINUTES_PER_HOUR;
    return rest === 0
      ? `${hours}h`
      : `${hours}h ${String(rest).padStart(2, "0")}m`;
  }
  return `${minutes} min`;
}

/**
 * Where the day stands, told the way someone glancing at the light
 * through the garage door would tell it — there is deliberately no wall
 * clock. "night" is the closed shop: nothing new starts, and time only
 * passes to finish what's already running.
 */
export type DayPhase = "morning" | "midday" | "afternoon" | "evening" | "night";

/** The phase of the day after `dayMinutes` of it have been spent. */
export function dayPhase(dayMinutes: number): DayPhase {
  if (dayMinutes >= MINUTES_PER_DAY) return "night";
  if (dayMinutes >= MINUTES_PER_DAY * 0.75) return "evening";
  if (dayMinutes >= MINUTES_PER_DAY * 0.5) return "afternoon";
  if (dayMinutes >= MINUTES_PER_DAY * 0.25) return "midday";
  return "morning";
}
